import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  IonModal,
  IonContent,
  IonButton,
  IonIcon,
  IonRow,
  IonCol,
  IonGrid,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonAvatar,
  IonThumbnail,
  IonNote,
  IonFooter,
  IonSpinner,
  useIonAlert,
} from '@ionic/react';
import BasePageWithHeader from '../../components/BasePageWithHeader';
import {
  addCircle,
  addOutline,
  chevronForwardOutline,
  closeOutline,
  searchOutline,
  locationOutline,
  trashOutline,
  personOutline,
  calendarOutline,
  timeOutline,
  timerOutline,
  repeatOutline,
  syncOutline,
  cubeOutline,
  peopleOutline,
  constructOutline,
} from 'ionicons/icons';
import ServiceTypeModal from '../../components/ServiceTypeModal'; // Import the ServiceTypeModal
import MiniCalendarPicker from '../../components/MiniCalendarPicker';
import GcalTimePicker from '../../components/GcalTimePicker';
import { ServiceType, AdditionalService } from '@rsmaids/shared/types/services'; // Import ServiceType
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { callAdminApi } from '@rsmaids/shared/services/api/wixAdminApi';
import { getCachedContact, getCachedContactList } from '@/utils/contactCache';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import styles from './Booking.module.scss';
import { Contact, ContactListItem, ContactEmail, ContactPhone, ContactAddress } from '@rsmaids/shared/types/contacts';
import { BookingItem, Resource, StaffMember } from '@rsmaids/shared/types/bookings';
import type { Booking } from '@rsmaids/shared/types/bookings';
import { ServiceInfo } from '@rsmaids/shared/types/services';
import { useAuth } from '../../contexts';
import { createServiceOrder, updateServiceOrder, getServiceOrder, confirmServiceOrder } from '../../services/zohoServiceOrderService';
import ServiceOrderModal, { type ServiceOrderDraft } from '../../components/ServiceOrderModal';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../../i18n/dynamic';
import { intlLocale } from '../../i18n/locale';

// Same portal-positioned overlay used by CreateInvoice.tsx's SelectField/
// DateField pickers and BlockedTimeForm.tsx's Staff/Availability pickers —
// positions a plain in-DOM dropdown by portaling it into a fixed, invisible
// root so it overlays the page instead of expanding the card it's anchored
// to. Dismiss uses pointerdown (touch app) on anything outside both the
// trigger and the portaled dropdown itself.
type PickerRect = { top: number; left?: number; right?: number; maxHeight: number; minWidth?: number };

// Scroll-driven repositioning writes straight to the portaled card's inline
// style via cardRef instead of through setState — a React re-render per
// scroll tick lags a frame or two behind the browser's own scroll/paint on
// mobile, which reads as the dropdown "floating"/detaching from its field
// mid-scroll. Direct DOM writes stay in lockstep with the scroll instead.
function usePickerDropdown(
  isOpen: boolean,
  fieldRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  close: () => void,
  align: 'left' | 'right',
  // Stretch to the trigger field's width (search/option-list dropdowns) vs.
  // size to its own natural content width (calendar/time pickers, which have
  // a fixed layout and look off-center/squashed if force-stretched).
  matchWidth = false,
) {
  const [rect, setRect] = useState<PickerRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): PickerRect | null => {
      const field = fieldRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      // How much room is actually left on THIS screen. The floating footer
      // (position:fixed price/submit card) covers the bottom of the viewport,
      // so "below the trigger" ends at the footer's top edge, not the window's.
      // Flip upward when the dropdown's real content height doesn't fit below
      // but there's more room above (same rule as Contact.tsx's PortalDropdown),
      // and cap max-height to whichever side it opens on so it never runs
      // under the footer or header.
      const footerEl = globalThis.document.querySelector('#booking-modal ion-footer');
      const headerEl = globalThis.document.querySelector('#booking-modal ion-header');
      const bottomLimit = footerEl ? footerEl.getBoundingClientRect().top : window.innerHeight;
      const topLimit = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
      const gap = 8;
      const spaceBelow = bottomLimit - box.bottom - gap;
      const spaceAbove = box.top - topLimit - gap;
      // scrollHeight = full content height regardless of the current inline max-height.
      // 0 on the very first paint (card not mounted yet) → opens downward, then the
      // double-rAF pass below re-runs this with the real height.
      const naturalHeight = cardRef.current?.scrollHeight ?? 0;
      const flipUp = naturalHeight > spaceBelow && spaceAbove > spaceBelow;
      const maxHeight = Math.max(120, Math.min(360, flipUp ? spaceAbove : spaceBelow));
      const top = flipUp
        ? box.top - anchor.top - Math.min(naturalHeight, maxHeight) - 4
        : box.bottom - anchor.top + 4;
      const minWidth = matchWidth ? box.width : undefined;
      return align === 'left'
        ? { top, left: box.left - anchor.left, maxHeight, minWidth }
        : { top, right: anchor.right - box.right, maxHeight, minWidth };
    };
    const applyRect = (r: PickerRect) => {
      const card = cardRef.current;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.right = r.right !== undefined ? `${r.right}px` : '';
      card.style.maxHeight = `${r.maxHeight}px`;
      card.style.minWidth = r.minWidth !== undefined ? `${r.minWidth}px` : '';

      // Keep the whole dropdown on screen horizontally. A wide picker (the clock) anchored to a
      // field near an edge used to hang off the screen — e.g. aligned to the RIGHT of a field in
      // the left column, it ran off the left side. Shift it back inside, with an 8px margin.
      const edge = 8;
      const box = card.getBoundingClientRect();
      let shift = 0;
      if (box.left < edge) shift = edge - box.left;
      else if (box.right > window.innerWidth - edge) shift = (window.innerWidth - edge) - box.right;
      if (shift !== 0) {
        if (r.left !== undefined) card.style.left = `${r.left + shift}px`;
        else if (r.right !== undefined) card.style.right = `${r.right - shift}px`;
      }
    };

    const initial = computeRect();
    setRect(initial);

    // Ionic's ion-item/ion-input are Stencil web components that can still be
    // mid-upgrade (shadow content not yet rendered) on the very first paint
    // after they're added to the DOM — a getBoundingClientRect() taken in
    // this same tick can read the host's pre-upgrade (much smaller) box. Two
    // animation frames is a standard, reliable way to wait past that without
    // guessing at a timeout.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    // The card doesn't exist until `rect` is set and React commits it, so its real
    // height (needed to decide whether to flip upward) can't be read at open time.
    // Watch it from the moment it mounts — the observer's first callback fires with
    // the measured size, which re-runs the flip/clamp without needing a scroll.
    const resizeObserver = new ResizeObserver(() => {
      const r = computeRect();
      if (r) applyRect(r);
    });
    let attachRaf = 0;
    const tryAttachObserver = () => {
      if (cardRef.current) { resizeObserver.observe(cardRef.current); return; }
      attachRaf = requestAnimationFrame(tryAttachObserver);
    };
    tryAttachObserver();

    const handleScroll = () => {
      const r = computeRect();
      if (r) applyRect(r);
    };
    const handleResize = () => {
      const r = computeRect();
      if (r) { setRect(r); applyRect(r); }
    };
    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (fieldRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancelAnimationFrame(attachRaf);
      resizeObserver.disconnect();
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, fieldRef, portalRef, cardRef, close, align, matchWidth]);

  return rect;
}

function PickerDropdown({
  isOpen, fieldRef, portalRef, close, align, className, children, matchWidth,
}: {
  isOpen: boolean;
  fieldRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  close: () => void;
  align: 'left' | 'right';
  className: string;
  matchWidth?: boolean;
  children: React.ReactNode;
}) {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePickerDropdown(isOpen, fieldRef, portalRef, cardRef, close, align, matchWidth);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);
  if (!isOpen || !rect || !portalNode) return null;
  return createPortal(
    <IonCard ref={cardRef} className={className} style={{ ...rect, position: 'absolute' }}>
      {children}
    </IonCard>,
    portalNode,
  );
}

const Booking: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  bookingId?: string;
  // Pre-selects a service type when opened straight from the Services
  // browser (Bookings.tsx) instead of the blank "Add New Booking" flow.
  initialServiceType?: ServiceType;
}> = ({ isOpen, onClose, bookingId, initialServiceType }) => {
  const { t: tr } = useTranslation();
  const isEditing = bookingId && bookingId !== 'new';
  const { authState } = useAuth();
  const { user } = authState;
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null); // Start with null for new bookings
  const [contact, setContact] = useState<BookingItem['contact'] | null>(null);
  const [showContactSearch, setShowContactSearch] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [allContacts, setAllContacts] = useState<ContactListItem[]>([]);
  const contactSearchRef = useRef<HTMLIonInputElement>(null);
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [contactAddresses, setContactAddresses] = useState<Contact['addresses']>([]);
  const [selectedBookingAddress, setSelectedBookingAddress] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [duration, setDuration] = useState<number>(0); // Start with no duration selected
  const [repeatOption, setRepeatOption] = useState<string>("Doesn't repeat");
  // How many times a repeating booking occurs (shown once a repeat is chosen)
  const [repeatCount, setRepeatCount] = useState<number>(4);
  const doesRepeat = repeatOption !== "Doesn't repeat";
  const REPEAT_COUNT_OPTIONS = [2, 3, 4, 5, 6]; // a repeating booking is limited to 6 occurrences
  // Days between occurrences for each repeat option ("Doesn't repeat" is not listed = 0)
  const REPEAT_INTERVAL_DAYS: Record<string, number> = {
    'Weekly': 7,
    'Bi-weekly': 14,
    'Every 3 weeks': 21,
    'Every 4 weeks': 28,
  };
  const [showRepeatCountPicker, setShowRepeatCountPicker] = useState(false);
  const repeatCountFieldRef = useRef<HTMLIonItemElement>(null);
  const closeRepeatCountPicker = useCallback(() => setShowRepeatCountPicker(false), []);
  const [serviceType, setServiceType] = useState<ServiceType | ''>(''); // Add state for service type
  const [serviceThumbnail, setServiceThumbnail] = useState<string>(''); // Add state for service thumbnail
  const [isServiceTypeModalOpen, setIsServiceTypeModalOpen] = useState(false); // Add state for service type modal
  const [additionalServices, setAdditionalServices] = useState<AdditionalService[]>([]); // Update state for additional services
  const [isAddingAdditionalService, setIsAddingAdditionalService] = useState<boolean>(false); // Flag for adding additional service
  const [currentAdditionalServiceIndex, setCurrentAdditionalServiceIndex] = useState<number>(-1); // Index for editing additional services
  const [resourceOption, setResourceOption] = useState<string>(''); // State for resource selection
  const [staffMembers, setStaffMembers] = useState<string[]>([]);
  const [showStaffPicker, setShowStaffPicker] = useState<boolean>(false);
  const [isAddingAdditionalStaff, setIsAddingAdditionalStaff] = useState<boolean>(false);
  const [currentStaffIndex, setCurrentStaffIndex] = useState<number>(0);
  const [allStaffMembers, setAllStaffMembers] = useState<StaffMember[]>([]);
  const [staffAvatarMap, setStaffAvatarMap] = useState<Map<string, string>>(new Map());
  const [staffFetchLoading, setStaffFetchLoading] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');
  const [notes, setNotes] = useState<string>('');
  const [notesAdded, setNotesAdded] = useState<boolean>(false);
  const [price, setPrice] = useState<number>(0); // Add state for price calculation
  const [serviceTypeOptions, setServiceTypeOptions] = useState<ServiceInfo[]>([]);
  const [serviceTypesLoading, setServiceTypesLoading] = useState(true);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isServiceOrderModalOpen, setIsServiceOrderModalOpen] = useState(false);
  const [soNumberError, setSoNumberError] = useState<string | undefined>();
  const [serviceOrderDraft, setServiceOrderDraft] = useState<ServiceOrderDraft | null>(null);
  const [taxAmount, setTaxAmount] = useState<number | null>(null);
  // Reset SO-derived price state (and, for new bookings, the date/time
  // picked in a previous session) whenever the form reopens or targets a
  // different booking — computed during render (React's "adjust state when
  // a dependency changes" pattern) rather than as synchronous setState
  // calls at the top of the mount-data effect below, which now only handles
  // the actual async contact/SO/booking loading.
  const [priceResetKey, setPriceResetKey] = useState<{ isOpen: boolean; isEditing: boolean; bookingId: string | undefined }>({ isOpen, isEditing: !!isEditing, bookingId });
  if (priceResetKey.isOpen !== isOpen || priceResetKey.isEditing !== !!isEditing || priceResetKey.bookingId !== bookingId) {
    setPriceResetKey({ isOpen, isEditing: !!isEditing, bookingId });
    setServiceOrderDraft(null);
    setPrice(0);
    setTaxAmount(null);
    if (bookingId === 'new' || !bookingId || !isEditing) {
      setSelectedDate(null);
      setSelectedTime('');
    }
  }
  const [catalogRate, setCatalogRate] = useState<number>(0);
  const [presentAlert] = useIonAlert();
  const [groupedResources, setGroupedResources] = useState<{ [category: string]: Resource[] }>({});
  const [allowedResourceTypeIds, setAllowedResourceTypeIds] = useState<string[]>([]);


  // Reference to the data service
  const dataService = DataServiceFactory.getService();

  // Duration/repeat/resource inline dropdown state
  const [showDurationPicker, setShowDurationPicker] = useState(false);
  const [showRepeatPicker, setShowRepeatPicker] = useState(false);
  const [showResourcePicker, setShowResourcePicker] = useState(false);

  // Portal target + field anchors for the floating PickerDropdowns
  const pickerPortalRef = useRef<HTMLDivElement>(null);
  // The floating footer is position:fixed, so its real height (which on native
  // includes the OS safe-area/nav-bar inset) is measured and published as
  // --booking-footer-h on the wrapper; .formContainer pads its bottom by it so
  // the last card always scrolls fully clear of the footer.
  const footerObserverRef = useRef<ResizeObserver | null>(null);
  const setFooterEl = useCallback((el: HTMLIonFooterElement | null) => {
    footerObserverRef.current?.disconnect();
    footerObserverRef.current = null;
    const host = el?.parentElement;
    if (!el || !host) return;
    const publish = () => host.style.setProperty('--booking-footer-h', `${el.getBoundingClientRect().height}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    footerObserverRef.current = ro;
  }, []);
  // Points at whichever of the "Select Contact"/"Change" button or the
  // inline search box is currently mounted (they swap, never both at once).
  const contactFieldRef = useRef<HTMLElement>(null);
  const addressFieldRef = useRef<HTMLIonItemElement>(null);
  const dateFieldRef = useRef<HTMLIonItemElement>(null);
  const timeFieldRef = useRef<HTMLIonItemElement>(null);
  const durationFieldRef = useRef<HTMLIonItemElement>(null);
  const repeatFieldRef = useRef<HTMLIonItemElement>(null);
  const resourceFieldRef = useRef<HTMLIonItemElement>(null);
  const staffFieldRef = useRef<HTMLIonItemElement>(null);
  const staffAddFieldRef = useRef<HTMLIonButtonElement>(null);

  const closeDatePicker = useCallback(() => setShowDatePicker(false), []);
  const closeTimePicker = useCallback(() => setShowTimePicker(false), []);
  const closeDurationPicker = useCallback(() => setShowDurationPicker(false), []);
  const closeRepeatPicker = useCallback(() => setShowRepeatPicker(false), []);
  const closeResourcePicker = useCallback(() => setShowResourcePicker(false), []);
  const closeStaffPicker = useCallback(() => { setShowStaffPicker(false); setStaffSearch(''); }, []);
  const closeAddressPicker = useCallback(() => { setShowAddressPicker(false); setShowCustomAddressInput(false); }, []);
  const closeContactPicker = useCallback(() => { setShowContactSearch(false); setContactSearch(''); }, []);

  // Helper function to convert time to HH:MM format
  const formatTimeValue = (timeInput: string | Date): string => {
    if (timeInput instanceof Date) {
      const hours = timeInput.getHours().toString().padStart(2, '0');
      const minutes = timeInput.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    if (typeof timeInput === 'string') {
      if (timeInput.includes('T')) {
        // ISO string format - convert to local time
        const dateObj = new Date(timeInput);
        const hours = dateObj.getHours().toString().padStart(2, '0');
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      }
      // Already in HH:MM format
      return timeInput.substring(0, 5);
    }

    return '';
  };

  // Helper function to calculate duration from start/end times
  const calculateDuration = (startTime: string | Date, endTime: string | Date): number => {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    const durationMs = endDate.getTime() - startDate.getTime();
    return Math.round(durationMs / (1000 * 60)); // Convert to minutes
  };


  // Check for contact data when component mounts
  useEffect(() => {
    const loadContactData = async () => {
      // Get contact data - it might be stored in different formats
      const storedData = DataStorageService.getData<{ id: string; name?: string } | Contact | null>('selectedContact');
      if (storedData) {
        DataStorageService.removeData('selectedContact');

        // Check cache first (same logic as Contact.tsx)
        const cacheKey = `contact-${storedData.id}`;
        const cached = localStorage.getItem(cacheKey);
        const cacheTime = localStorage.getItem(`${cacheKey}-time`);
        const isCacheValid = cached && cacheTime &&
          (Date.now() - parseInt(cacheTime)) < 5 * 60 * 1000; // 5 minutes

        if (isCacheValid) {
          try {
            const fullContact = JSON.parse(cached);
            console.log('Using cached contact data for booking');
            setContact(createBookingContact(fullContact));
            setContactAddresses(fullContact.addresses || []);
            return;
          } catch {
            // Cache corrupted, continue to API call
          }
        }

        // Cache miss or API fallback
        if (!('firstName' in storedData) || !('emails' in storedData)) {
          try {
            const result = await getCachedContact(storedData.id, () => dataService.contacts.getContactById(storedData.id));
            if (result.data) {
              setContact(createBookingContact(result.data));
              setContactAddresses(result.data.addresses || []);
            }
          } catch (error) {
            console.error('Error fetching full contact:', error);
            setContact({
              id: storedData.id,
              name: storedData.name || '',
              email: '',
              phone: '',
              address: '',
              avatarUrl: undefined
            });
          }
        } else {
          // Full Contact object available
          setContact(createBookingContact(storedData));
          setContactAddresses(storedData.addresses || []);
        }
      }
    };

    // Helper to create booking contact format
    const createBookingContact = (contact: Contact) => ({
      id: contact.id,
      name: `${contact.firstName} ${contact.lastName}`.trim(),
      email: contact.emails?.find((e: ContactEmail) => e.primary)?.email || contact.emails?.[0]?.email || '',
      phone: contact.phones?.find((p: ContactPhone) => p.primary)?.phone || contact.phones?.[0]?.phone || '',
      address: contact.addresses?.length > 0 ? (() => {
        const primaryAddr = contact.addresses.find((a: ContactAddress) => a.primary) || contact.addresses[0];
        const addr = primaryAddr.address;
        return [addr.addressLine1, addr.addressLine2, addr.city, addr.state, addr.postalCode, addr.country]
          .filter(Boolean)
          .join(', ');
      })() : '',
      avatarUrl: contact.avatar
    });

    loadContactData();

    // Auto-load existing SO price/tax for edit mode so the footer shows correct totals immediately
    if (isEditing && bookingId) {
      getServiceOrder(bookingId).then(existing => {
        if (!existing) return;
        const total = existing.total ?? 0;
        const taxAmt = (existing.line_items ?? []).reduce((sum, li) => {
          const lineAmt = li.quantity * li.rate;
          const discAmt = li.discount ? lineAmt * (li.discount / 100) : 0;
          const net = lineAmt - discAmt;
          const pct = li.tax_percentage ?? 0;
          return sum + (existing.is_inclusive_tax
            ? net - net / (1 + pct / 100)
            : net * (pct / 100));
        }, 0);
        // Build the draft so price stays locked even if duration changes
        const draft: ServiceOrderDraft = {
          soNumber:      existing.salesorder_number ?? '',
          soDate:        existing.date ?? new Date().toISOString().split('T')[0],
          paymentTerms:  existing.payment_terms ?? 3,
          paymentTermsLabel: existing.payment_terms_label ?? 'Net 3',
          serviceMonth:  '',
          isInclusiveTax: existing.is_inclusive_tax ?? true,
          pricelistId:   existing.pricelist_id ?? '',
          lineItems:     (existing.line_items ?? []).map(li => ({
            item_id:     li.item_id     || undefined,
            name:        li.name        || '',
            description: li.description || '',
            quantity:    li.quantity    || 1,
            rate:        li.rate        || 0,
            tax_id:      li.tax_id      || undefined,
            tax_name:    li.tax_name    || undefined,
            discount:    li.discount    || undefined,
            discountType: li.discount ? '%' : undefined,
          })),
          notes:     existing.customer_notes ?? '',
          terms:     existing.terms ?? '',
          total,
          taxAmount: taxAmt,
          action:    'confirm',
        };
        setServiceOrderDraft(draft);
        setPrice(total);
        setTaxAmount(taxAmt);
      }).catch(() => { /* no SO yet — leave price at 0 */ });
    }

    // Handle date and booking data loading. Deferred via a resolved-promise
    // microtask (not called synchronously in the effect body) — this logic
    // is entirely synchronous itself, but the effect-setState rule flags
    // any setState reachable directly at an effect's top level.
    Promise.resolve().then(() => {
      const storedDate = DataStorageService.getData<string>('bookingSelectedDate');

      if (bookingId === 'new' || !bookingId || !isEditing) {
        // Clear data for new bookings
        DataStorageService.removeData('bookingSelectedDate');
      } else if (isEditing) {
        // Load stored booking data for editing - but only after service types are loaded
        const storedBookingData = DataStorageService.getData<BookingItem>(`edit-booking-${bookingId}`);
        if (storedBookingData) {
          // Wait for service types to be loaded before setting thumbnail
          // (both branches restore the same fields — the thumbnail-specific
          // effect elsewhere picks up serviceType once options are ready)
          setContact(storedBookingData.contact);
          setServiceType(storedBookingData.serviceType);
          // Use startTime (full datetime) for the date to avoid UTC midnight timezone shift
          const startAsDate = storedBookingData.startTime ? new Date(storedBookingData.startTime) : new Date(storedBookingData.date);
          setSelectedDate(startAsDate);

          const timeValue = formatTimeValue(storedBookingData.startTime);
          if (timeValue) {
            setSelectedTime(timeValue);
          }

          let durationValue = storedBookingData.duration || 0;
          if (durationValue === 0 && storedBookingData.startTime && storedBookingData.endTime) {
            durationValue = calculateDuration(storedBookingData.startTime, storedBookingData.endTime);
          }

          setDuration(durationValue);
          setStaffMembers(Array.isArray(storedBookingData.staff) ? storedBookingData.staff : [storedBookingData.staff || '']);
          setResourceOption(storedBookingData.resource || '');
          setNotes(storedBookingData.notes || '');
          setPrice(storedBookingData.price || 0);
          setSelectedBookingAddress(storedBookingData.contact?.address || '');
        }
      } else if (storedDate) {
        // Load from stored date
        DataStorageService.removeData('bookingSelectedDate');
        setSelectedDate(new Date(storedDate));

        const dateObj = new Date(storedDate);
        const timeValue = formatTimeValue(dateObj);
        setSelectedTime(timeValue);
      } else {
        // Default state
        setSelectedDate(null);
        setSelectedTime('');
      }
    });
  }, [bookingId, isEditing, dataService, isOpen]);

  // When editing, automatically fetch the existing SO to populate price/taxAmount in the footer
  useEffect(() => {
    if (!isOpen || !isEditing || !bookingId) return;
    let cancelled = false;
    const loadSoPrice = async () => {
      try {
        const existing = await getServiceOrder(bookingId);
        if (cancelled || !existing) return;
        // Build a minimal draft so the price effect guard kicks in
        const lineItems = (existing.line_items ?? []).map(li => ({
          item_id:     li.item_id     || undefined,
          name:        li.name        || '',
          description: li.description || '',
          quantity:    li.quantity    || 1,
          rate:        li.rate        || 0,
          tax_id:      li.tax_id      || undefined,
          tax_name:    li.tax_name    || undefined,
          tax_percentage: li.tax_percentage || 0,
        }));
        const draft: ServiceOrderDraft = {
          soNumber:      existing.salesorder_number ?? '',
          soDate:        existing.date ?? new Date().toISOString().split('T')[0],
          paymentTerms:  existing.payment_terms ?? 3,
          paymentTermsLabel: existing.payment_terms_label ?? 'Net 3',
          serviceMonth:  '',
          isInclusiveTax: existing.is_inclusive_tax ?? true,
          pricelistId:   existing.pricelist_id ?? '',
          lineItems,
          notes:     existing.customer_notes ?? '',
          terms:     existing.terms ?? '',
          total:     existing.total ?? 0,
          taxAmount: 0,
          action:    'confirm',
        };
        setServiceOrderDraft(draft);
        setPrice(existing.total ?? 0);
        // taxAmount will be recalculated in the SO modal; use 0 as placeholder until modal is opened
        setTaxAmount(null);
      } catch {
        // SO may not exist yet — that's fine, leave price as 0
      }
    };
    void loadSoPrice();
    return () => { cancelled = true; };
  }, [isOpen, isEditing, bookingId]);

  // Recalculates the default price from duration/serviceType/additionalServices
  // whenever any of those change — computed during render (React's "adjust
  // state when a dependency changes" pattern) rather than as setState calls
  // inside an effect, since this is a pure derivation with no external
  // system involved. `price` still stays real state (not folded into a
  // useMemo) because it's also set directly elsewhere from async sources
  // (stored booking data, a loaded service order) that this calculation
  // must yield to, not overwrite.
  const [prevPriceCalcInputs, setPrevPriceCalcInputs] = useState({ serviceType, duration, additionalServices, serviceOrderDraft });
  if (
    prevPriceCalcInputs.serviceType !== serviceType ||
    prevPriceCalcInputs.duration !== duration ||
    prevPriceCalcInputs.additionalServices !== additionalServices ||
    prevPriceCalcInputs.serviceOrderDraft !== serviceOrderDraft
  ) {
    setPrevPriceCalcInputs({ serviceType, duration, additionalServices, serviceOrderDraft });
    // If a service order draft exists, its total is the authoritative price — never overwrite it
    if (!serviceOrderDraft) {
      if (duration === 0) {
        setPrice(0);
      } else {
        let basePrice = 0;
        if (serviceType && serviceType.includes('Recurring')) {
          basePrice = 50;
        } else if (serviceType && serviceType.includes('Deep')) {
          basePrice = 60;
        } else if (serviceType && (serviceType.includes('Move') || serviceType.includes('Out'))) {
          basePrice = 70;
        } else {
          basePrice = 45;
        }

        const hours = duration / 60;
        let totalPrice = basePrice * hours;

        // Add cost for additional services
        additionalServices.forEach(service => {
          let additionalCost = 15;
          if (service.type.includes('Deep')) {
            additionalCost = 25;
          } else if (service.type.includes('Move') || service.type.includes('Out')) {
            additionalCost = 20;
          }
          totalPrice += additionalCost;
        });

        setPrice(totalPrice);
      }
    }
  }

  const handleClose = useCallback(() => {
    // This form stays mounted between opens (the parent toggles `isOpen` on
    // an always-rendered <Booking>, never unmounts it) — so without this,
    // a contact picked for a new booking that's closed without saving was
    // still sitting in state the next time the form opened. Only reset for
    // the new-booking flow; editing an existing booking reloads its own
    // contact from bookingId on the next open regardless.
    if (!isEditing) {
      setContact(null);
      setContactAddresses([]);
    }
    onClose();
  }, [onClose, isEditing]);

  const handleContactSelect = async (selectedContact: ContactListItem) => {
    setShowContactSearch(false);
    setContactSearch('');
    try {
      const result = await getCachedContact(selectedContact.id, () => dataService.contacts.getContactById(selectedContact.id));

      if (result.data) {
        // Store the full contact addresses for address selection
        setContactAddresses(result.data.addresses || []);

        // Convert Contact to BookingItem['contact'] format
        const bookingContact: BookingItem['contact'] = {
          id: result.data.id,
          name: `${result.data.firstName} ${result.data.lastName}`.trim(),
          email: result.data.emails?.find((e: ContactEmail) => e.primary)?.email || result.data.emails?.[0]?.email || '',
          phone: result.data.phones?.find((p: ContactPhone) => p.primary)?.phone || result.data.phones?.[0]?.phone || '',
          address: result.data.addresses?.length > 0 ? (() => {
            const primaryAddr = result.data.addresses.find((a: ContactAddress) => a.primary) || result.data.addresses[0];
            const addr = primaryAddr.address;
            return [addr.addressLine1, addr.addressLine2, addr.city, addr.state, addr.postalCode, addr.country]
              .filter(Boolean)
              .join(', ');
          })() : '',
          avatarUrl: result.data.avatar
        };

        // Set the contact for display in the booking form
        setContact(bookingContact);

        // Set the initial booking address to the contact's primary address
        setSelectedBookingAddress(bookingContact.address);
      }
    } catch (error) {
      console.error('Error fetching contact data:', error);
    }
  };

  // State for custom address input
  const [showCustomAddressInput, setShowCustomAddressInput] = useState(false);
  const [customAddressValue, setCustomAddressValue] = useState('');

  // Handle custom address submission
  const handleCustomAddressSubmit = () => {
    if (customAddressValue.trim()) {
      setSelectedBookingAddress(customAddressValue.trim());
      if (contact) {
        setContact({
          ...contact,
          address: customAddressValue.trim()
        });
      }
      setShowCustomAddressInput(false);
      setCustomAddressValue('');
      setShowAddressPicker(false);
    }
  };

  useEffect(() => {
    // Cleanup function
    return () => {
      DataStorageService.storeData('calendarShouldRefresh', true);
    };
  }, []);
  // Handle time selection from popover
  const handleTimeSelect = (timeString: string) => {
    // Set the selected time string first
    setSelectedTime(timeString);

    // Update the full date with the new time (only if we have a selected date)
    if (selectedDate) {
      const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
      const newDate = new Date(selectedDate);
      newDate.setHours(hours, minutes, 0, 0);

      // Update the selected date
      setSelectedDate(newDate);
    }

    // Close the popover
    setShowTimePicker(false);
  };


  const handleServiceTypeSelectWithIndex = (type: ServiceType, imageUrl: string) => {
    // Ensure we always have a valid image URL
    const validImageUrl = imageUrl || '/assets/images/bucket.svg';

    if (isAddingAdditionalService) {
      if (currentAdditionalServiceIndex !== -1) {
        // Update existing service
        const updatedServices = [...additionalServices];
        updatedServices[currentAdditionalServiceIndex] = {
          type: type,
          thumbnail: validImageUrl
        };
        setAdditionalServices(updatedServices);
        setCurrentAdditionalServiceIndex(-1);
      } else {
        // Add new service
        setAdditionalServices([...additionalServices, {
          type: type,
          thumbnail: validImageUrl
        }]);
      }
      setIsAddingAdditionalService(false);
    } else {
      // Set the main service type
      setServiceType(type);
      setServiceThumbnail(validImageUrl);

      // Auto-set duration from this service's Wix default if not already set by a stored booking
      const normalize = (s: string) => s.toLowerCase().replace(/[-\s]+/g, ' ').trim();
      const matchedService = serviceTypeOptions.find(s => normalize(s.type) === normalize(type));
      if (matchedService?.defaultDuration && matchedService.defaultDuration > 0) {
        setDuration(matchedService.defaultDuration);
      }

      // If SO draft already confirmed (new flow: SO modal → Wix picker), we're done
      if (serviceOrderDraft) {
        setIsServiceTypeModalOpen(false);
        return;
      }

      // Legacy path (editing with no draft, or SO modal skipped): open SO modal
      setCatalogRate(0);
      setServiceOrderDraft(null);

      if (isEditing && bookingId) {
        setIsSaving(true);
        getServiceOrder(bookingId)
          .then(existing => {
            if (existing) {
              presentAlert({
                header: tr('pages.bookingSystem.BookingForm.serviceOrderExists'),
                message: tr('pages.bookingSystem.BookingForm.aServiceOrderAlreadyExistsFor', { salesorder_number: existing.salesorder_number }),
                buttons: ['OK'],
              });
            } else {
              setIsServiceOrderModalOpen(true);
            }
          })
          .catch(() => setIsServiceOrderModalOpen(true))
          .finally(() => setIsSaving(false));
      } else {
        setIsServiceOrderModalOpen(true);
      }
    }

    setIsServiceTypeModalOpen(false);
  };
  // The schedule (date + start time) must be filled in before a NEW booking's service and order
  // can be chosen. Editing an existing booking already has one.
  const isScheduleSet = isEditing || (!!selectedDate && !!selectedTime);

  // Opens the SO modal — for edit mode, loads the existing SO from Zoho first so it pre-populates
  const openServiceOrderModal = async () => {
    if (isEditing && bookingId && !serviceOrderDraft) {
      setIsSaving(true);
      try {
        const existing = await getServiceOrder(bookingId);
        console.log('[openServiceOrderModal] getServiceOrder result:', existing);
        if (existing) {
          // Map Zoho SO fields into a ServiceOrderDraft so the modal pre-populates
          const draft: ServiceOrderDraft = {
            soNumber:      existing.salesorder_number ?? '',
            soDate:        existing.date ?? new Date().toISOString().split('T')[0],
            paymentTerms:  existing.payment_terms ?? 3,
            paymentTermsLabel: existing.payment_terms_label ?? 'Net 3',
            serviceMonth:  '',
            isInclusiveTax: existing.is_inclusive_tax ?? true,
            pricelistId:   existing.pricelist_id ?? '',
            lineItems:     (existing.line_items ?? []).map(li => ({
              item_id:      li.item_id     || undefined,
              name:         li.name        || '',
              description:  li.description || '',
              quantity:     li.quantity    || 1,
              rate:         li.rate        || 0,
              tax_id:       li.tax_id      || undefined,
              tax_name:     li.tax_name    || undefined,
              // Zoho returns discount as a number. discount_type on line items from Zoho
              // is typically absent or 'item_level' — we default to '$' (flat amount)
              // since that is the standard usage. '%' is only used when Zoho explicitly
              // signals a percentage type at the line-item level.
              discount:     li.discount    || undefined,
              discountType: (li.discount
                ? (li.discount_type === 'percentage' ? '%' : '$')
                : undefined),
            })),
            notes:     existing.customer_notes ?? '',
            terms:     existing.terms ?? '',
            total:     existing.total ?? 0,
            // Compute tax from line items so the footer shows the correct split
            taxAmount: (() => {
              const items = existing.line_items ?? [];
              return items.reduce((sum, li) => {
                const lineAmt = li.quantity * li.rate;
                // Respect discount_type: '%' = percentage, '$' (or unknown) = flat dollar
                const discType = li.discount_type === 'percentage' ? '%' : '$';
                const discAmt = li.discount
                  ? (discType === '%' ? lineAmt * (li.discount / 100) : li.discount)
                  : 0;
                const net = lineAmt - discAmt;
                const pct = li.tax_percentage ?? 0;
                const taxAmt = existing.is_inclusive_tax
                  ? net - net / (1 + pct / 100)
                  : net * (pct / 100);
                return sum + taxAmt;
              }, 0);
            })(),
            action:    'confirm',
          };
          setServiceOrderDraft(draft);
          setPrice(draft.total ?? 0);
          setTaxAmount(draft.taxAmount ?? null);
        }
      } catch (err) {
        console.error('[openServiceOrderModal] getServiceOrder failed:', err);
        // If fetch fails, open modal with no pre-population
      } finally {
        setIsSaving(false);
      }
    }
    setIsServiceOrderModalOpen(true);
  };

  const handleServiceOrderConfirm = (draft: ServiceOrderDraft) => {
    setServiceOrderDraft(draft);
    setPrice(draft.total);
    setTaxAmount(draft.taxAmount);

    // Auto-select the Wix service by matching the first SO line item name
    const firstItemName = draft.lineItems?.[0]?.name?.toLowerCase() ?? '';
    if (firstItemName) {
      const keywordMap: Array<{ keywords: string[]; service: ServiceType }> = [
        { keywords: ['deep clean', 'deep-clean'], service: 'Deep Cleaning' },
        { keywords: ['move out', 'move-out', 'moveout'], service: 'Move Out Cleaning' },
        { keywords: ['move in', 'move-in', 'movein'], service: 'Move Out Cleaning' },
        { keywords: ['post construction', 'post-construction', 'postconstruction'], service: 'Post Construction Cleaning' },
        { keywords: ['window'], service: 'Window Cleaning' },
        { keywords: ['oven'], service: 'Oven Cleaning' },
        { keywords: ['refrigerator', 'fridge'], service: 'Refrigerator Cleaning' },
        { keywords: ['airbnb', 'air bnb', 'vacation'], service: 'AirBnb Cleaning Service' },
        { keywords: ['commercial'], service: 'Commercial Cleaning' },
        { keywords: ['office'], service: 'Office Cleaning' },
        { keywords: ['estimate'], service: 'Free Estimate' },
        { keywords: ['recurring', 'regular', 'standard'], service: 'Recurring Cleaning' },
      ];
      const entry = keywordMap.find(e => e.keywords.some(k => firstItemName.includes(k)));
      if (entry?.service) {
        const normSvc = (s: string) => s.toLowerCase().replace(/[-\s]+/g, ' ').trim();
        const matched = serviceTypeOptions.find(s => normSvc(s.type) === normSvc(entry.service));
        setServiceType(entry.service);
        // Auto-set duration from Wix default for this service
        if (matched?.defaultDuration && matched.defaultDuration > 0) {
          setDuration(matched.defaultDuration);
        }
        setServiceThumbnail(matched?.imageUrl || '/assets/images/bucket.svg');
      }
    }
    // Draft saved — Zoho SO will be created when the booking is submitted
  };

  const handleRemoveAdditionalService = (index: number) => {
    const updatedServices = [...additionalServices];
    updatedServices.splice(index, 1);
    setAdditionalServices(updatedServices);
  };


  // Handler for staff member selection
  const handleStaffMemberSelect = (staff: string) => {
    if (isAddingAdditionalStaff) {
      // Add to additional staff array
      setStaffMembers([...staffMembers, staff]);
    } else {
      // Set or update existing staff
      const updatedStaff = [...staffMembers];
      updatedStaff[currentStaffIndex] = staff;
      setStaffMembers(updatedStaff);
    }
    setIsAddingAdditionalStaff(false);
    setShowStaffPicker(false);
    setStaffSearch('');
  };

  // Handler for adding another staff member
  const handleAddStaffMember = () => {
    setIsAddingAdditionalStaff(true);
    setStaffSearch('');
    setShowStaffPicker(true);
  };

  // Handler for removing a staff member
  const handleRemoveStaffMember = (indexToRemove: number) => {
    setStaffMembers(staffMembers.filter((_, index) => index !== indexToRemove));
  };

  // Function to handle adding notes
  const handleAddNotes = () => {
    setNotesAdded(true);
  };

  // "Add Note" in Booking Details opens this form for editing and flags it: show the notes box,
  // scroll the Notes card into view and put the cursor in it.
  const notesCardRef = useRef<HTMLIonCardElement>(null);
  const notesTextareaRef = useRef<HTMLIonTextareaElement>(null);
  // True when the form was opened by "Add Note": saving then only updates the booking itself
  // (its staff notes, in Wix) and never touches the Zoho service order.
  const notesOnlyEditRef = useRef(false);

  // The booking's notes as Wix has them (the booking's calendar event notes). Loaded when an
  // existing booking is opened, and compared on save so they are only written when changed.
  type BookingNotesApi = { getBookingNotes: (id: string) => Promise<string>; updateBookingNotes: (id: string, notes: string) => Promise<void> };
  const bookingNotesApi = dataService.bookings as unknown as BookingNotesApi;
  const loadedWixNotesRef = useRef('');
  useEffect(() => {
    if (!isOpen || !isEditing || !bookingId) return;
    let cancelled = false;
    loadedWixNotesRef.current = '';
    bookingNotesApi.getBookingNotes(bookingId).then(wixNotes => {
      if (cancelled) return;
      loadedWixNotesRef.current = wixNotes;
      if (wixNotes) {
        setNotes(wixNotes);
        setNotesAdded(true);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the api object is stable per dataService
  }, [isOpen, isEditing, bookingId]);
  useEffect(() => {
    if (!isOpen) notesOnlyEditRef.current = false;
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || !isEditing || !bookingId) return;
    if (DataStorageService.getData<string>('edit-booking-focus-notes') !== bookingId) return;
    DataStorageService.removeData('edit-booking-focus-notes');
    notesOnlyEditRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot reaction to the flag above
    setNotesAdded(true);
    const timer = setTimeout(() => {
      notesCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      notesTextareaRef.current?.setFocus();
    }, 500);
    return () => clearTimeout(timer);
  }, [isOpen, isEditing, bookingId]);

  // Function to handle deleting notes
  const handleDeleteNotes = () => {
    setNotes('');
    setNotesAdded(false);
  };

  // Function to handle booking creation/update
  const handleCreateBooking = async () => {
    if (!contact || !serviceType || !resourceOption || staffMembers.length === 0 || !selectedTime || duration === 0 || !selectedDate) {
      console.error('Missing required booking information');
      return;
    }

    setIsSaving(true);
    try {
      const _y = selectedDate.getFullYear();
      const _m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const _d = String(selectedDate.getDate()).padStart(2, '0');
      const localDateStr = `${_y}-${_m}-${_d}`;
      const startDateTime = new Date(`${localDateStr}T${selectedTime}`);
      const endDateTime = new Date(startDateTime.getTime() + duration * 60 * 1000); // Add duration in milliseconds

      const bookingData = {
        contactId: contact.id,
        title: `${serviceType} - ${contact.name}`,
        serviceType: serviceType as ServiceType,
        startTime: startDateTime,
        endTime: endDateTime,
        date: localDateStr,
        location: selectedBookingAddress || contact.address,
        notes: notes,
        staff: staffMembers.join(', '), // Join multiple staff members
        resource: resourceOption,
        resourceTypeIds: allowedResourceTypeIds, // Pass the allowed resource types from service
        duration: duration,
        price: price,
        status: 'confirmed' as const,
        contact: contact,
        isPaid: false,
        isCompleted: false,
        frequency: 'one-time' as const
      };

      if (isEditing && bookingId) {
        // For update, we need to get the existing booking first
        const existingBooking = await dataService.bookings.getBookingById(bookingId);
        if (existingBooking) {
          const updatedBooking: Booking = {
            ...existingBooking,
            ...bookingData,
            id: existingBooking.id
          };
          // The Notes card on this form is the booking's own (staff) notes, stored on the Wix
          // booking only — it never goes to the Zoho service order. So when nothing the order
          // is built from changed (service, date/time, duration, resource, staff, address, or an
          // edited order), skip the Zoho update altogether. Before, a notes-only edit still
          // called Zoho, which refuses to touch an already-invoiced order, and the user got a
          // "Service Order Error" for a change that has nothing to do with Zoho.
          const sameText = (a: unknown, b: unknown) => String(a ?? '').trim() === String(b ?? '').trim();
          const existingStart = existingBooking.startTime ? new Date(existingBooking.startTime).getTime() : NaN;
          const existingEnd = existingBooking.endTime ? new Date(existingBooking.endTime).getTime() : NaN;
          const existingDuration = existingBooking.duration || (
            Number.isFinite(existingStart) && Number.isFinite(existingEnd) ? Math.round((existingEnd - existingStart) / 60000) : 0
          );
          const rawExistingStaff: unknown = existingBooking.staff;
          const existingStaff = Array.isArray(rawExistingStaff) ? rawExistingStaff.join(', ') : rawExistingStaff;
          const orderInputsChanged =
            !!serviceOrderDraft ||
            !sameText(existingBooking.serviceType, serviceType) ||
            existingStart !== startDateTime.getTime() ||
            existingEnd !== endDateTime.getTime() ||
            existingDuration !== duration ||
            !sameText(existingBooking.resource, resourceOption) ||
            !sameText(existingStaff, staffMembers.join(', ')) ||
            !sameText(existingBooking.location, selectedBookingAddress || contact.address);

          // Free Estimate is never a billable item — no Zoho SO for it
          // Opened by "Add Note": these are staff notes on the booking, so no Zoho at all.
          if (serviceType !== 'Free Estimate' && orderInputsChanged && !notesOnlyEditRef.current) {
            // Try to update the Zoho SO first — ask user if it fails
            try {
              await updateServiceOrder({
                bookingId:    bookingId,
                contactName:  contact?.name  || 'Guest',
                contactEmail: contact?.email || undefined,
                serviceName:  serviceType   || 'Cleaning Service',
                startDate:    startDateTime.toISOString(),
                endDate:      endDateTime.toISOString(),
                duration:     duration || undefined,
                resource:     resourceOption || undefined,
                staffMembers: staffMembers.length ? staffMembers : undefined,
                address:      selectedBookingAddress || contact?.address || undefined,
                // Include draft changes if user edited the SO modal
                ...(serviceOrderDraft ? {
                  draftLineItems:           serviceOrderDraft.lineItems,
                  soNumber:                 serviceOrderDraft.soNumber,
                  soDate:                   serviceOrderDraft.soDate,
                  paymentTerms:             serviceOrderDraft.paymentTerms,
                  paymentTermsLabel:        serviceOrderDraft.paymentTermsLabel,
                  serviceMonth:             serviceOrderDraft.serviceMonth,
                  serviceMonthCustomFieldId: serviceOrderDraft.serviceMonthCustomFieldId,
                  isInclusiveTax:           serviceOrderDraft.isInclusiveTax,
                  pricelistId:              serviceOrderDraft.pricelistId,
                  soStatus:                 serviceOrderDraft.action === 'confirm' ? 'confirmed' : 'draft',
                  customerNotes:            serviceOrderDraft.notes || undefined,
                  terms:                    serviceOrderDraft.terms,
                  notes:                    serviceOrderDraft.notes || undefined,
                  price:                    serviceOrderDraft.total,
                } : {}),
              });
            } catch (zohoErr) {
              console.error('[BookingForm] Zoho SO update failed:', zohoErr);
              const continueAnyway = await new Promise<boolean>(resolve => {
                presentAlert({
                  header: tr('pages.bookingSystem.BookingForm.serviceOrderError'),
                  message: tr('pages.bookingSystem.BookingForm.theServiceOrderCouldNotBe'),
                  cssClass: 'logout-alert',
                  buttons: [
                    { text: tr('pages.bookingSystem.BookingForm.cancel'), role: 'cancel', handler: () => resolve(false) },
                    { text: tr('pages.bookingSystem.BookingForm.continueAnyway'), handler: () => resolve(true) },
                  ],
                });
              });
              if (!continueAnyway) return;
            }
          }

          // "Add Note" only changes the notes, so it does not reschedule the booking at all.
          if (!notesOnlyEditRef.current) {
            await dataService.bookings.updateBooking(updatedBooking);
            console.log('Booking updated successfully');
          }

          // The booking's notes go to Wix on their own (the booking's calendar event notes) —
          // updateBooking above cannot change them.
          if (notes.trim() !== loadedWixNotesRef.current.trim()) {
            try {
              await bookingNotesApi.updateBookingNotes(bookingId, notes.trim());
              loadedWixNotesRef.current = notes.trim();
            } catch (notesErr) {
              console.error('[BookingForm] Could not save the booking notes to Wix:', notesErr);
              presentAlert({
                header: tr('pages.bookingSystem.BookingForm.notesNotSaved'),
                message: tr('pages.bookingSystem.BookingForm.theBookingWasSavedButIts'),
                cssClass: 'logout-alert', // the app's glass alert style, same as the other alerts in this form
                buttons: ['OK'],
              });
              return;
            }
          }

          // Notify all tabs that a booking was updated
          console.log('🚀 BOOKING: Sending refresh trigger signal for update');
          DataStorageService.storeData('booking-refresh-trigger', Date.now().toString());
          DataStorageService.notifyBookingChanged();
          console.log('🚀 BOOKING: Update refresh signal sent successfully');

          // Give Wix API MORE time to process and propagate the changes (especially for resource updates)
          console.log('⏳ Waiting 2 seconds for Wix to propagate resource changes...');
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } else {
        // A repeating booking is one booking per occurrence, each REPEAT_INTERVAL_DAYS after the last
        // (Wix has no booking series). "Doesn't repeat" = a single occurrence.
        const intervalDays = REPEAT_INTERVAL_DAYS[repeatOption] ?? 0;
        const occurrences = intervalDays > 0 ? repeatCount : 1;
        let createdCount = 0;
        let failedOn: { date: Date; reason: string } | null = null;

        for (let i = 0; i < occurrences; i++) {
          // Moved by calendar days (not 24h blocks), so the clock time stays the same across a DST change
          const occStart = new Date(startDateTime);
          occStart.setDate(occStart.getDate() + i * intervalDays);
          const occEnd = new Date(occStart.getTime() + duration * 60 * 1000);
          const occDateStr = `${occStart.getFullYear()}-${String(occStart.getMonth() + 1).padStart(2, '0')}-${String(occStart.getDate()).padStart(2, '0')}`;
          const occurrenceData = { ...bookingData, startTime: occStart, endTime: occEnd, date: occDateStr };

          // Create new booking with user role for admin override capabilities
          const result = await dataService.bookings.createBookingWithAvailabilityCheck(occurrenceData, {
            userRole: user?.role as 'owner' | 'admin' | 'manager' | 'user'
          });

          if (result.error) {
            // The first one failing is the whole save failing, as before. A later one failing stops the
            // series there (the ones already made stay) and is reported below.
            if (i === 0) throw new Error(result.error);
            failedOn = { date: occStart, reason: result.error };
            break;
          }
          createdCount++;

          console.log('Booking created successfully with status:', result.availabilityStatus);

          // Create the linked Zoho Books Service Order in the background.
          // Non-blocking — a failure here must not break the booking.
          // Free Estimate is never a billable item — no Zoho SO for it.
          const resultObj = result as Record<string, unknown>;
          const bookingObj = resultObj['booking'] as Record<string, unknown> | undefined;
          // result.booking is { booking: { _id: "...", ... } } — nested one level deep
          const innerBooking = (bookingObj?.['booking'] ?? bookingObj) as Record<string, unknown> | undefined;
          const wixBookingId: string = (innerBooking?.['_id'] ?? innerBooking?.['id'] ?? bookingObj?.['_id'] ?? resultObj['bookingId'] ?? resultObj['id'] ?? '') as string;
          if (wixBookingId && serviceType !== 'Free Estimate') {
            // For the 2nd+ occurrence of a series: no fixed SO number (each order needs its own — Zoho
            // numbers them), and its date and service month are that occurrence's own.
            const isLaterOccurrence = i > 0;
            // Create the SO now using the real Wix booking ID and the saved draft
            createServiceOrder({
              bookingId:                wixBookingId,
              contactName:              contact.name  || 'Guest',
              contactEmail:             contact.email || undefined,
              contactPhone:             contact.phone || undefined,
              serviceName:              serviceType   || 'Cleaning Service',
              startDate:                occStart.toISOString(),
              endDate:                  occEnd.toISOString(),
              duration:                 duration || undefined,
              resource:                 resourceOption || undefined,
              staffMembers:             staffMembers.length ? staffMembers : undefined,
              address:                  selectedBookingAddress || contact.address || undefined,
              // Only the service order's OWN notes (from its editor). The booking's staff notes
              // (the Notes card on this form) are Wix-only and must never reach Zoho.
              notes:                    serviceOrderDraft?.notes || undefined,
              ...(serviceOrderDraft ? {
                draftLineItems:           serviceOrderDraft.lineItems,
                soNumber:                 isLaterOccurrence ? undefined : serviceOrderDraft.soNumber,
                soDate:                   isLaterOccurrence ? occDateStr : serviceOrderDraft.soDate,
                paymentTerms:             serviceOrderDraft.paymentTerms,
                paymentTermsLabel:        serviceOrderDraft.paymentTermsLabel,
                serviceMonth:             isLaterOccurrence
                  ? occStart.toLocaleDateString('en-US', { month: 'long' }) // i18n-ignore: saved to Zoho
                  : serviceOrderDraft.serviceMonth,
                serviceMonthCustomFieldId: serviceOrderDraft.serviceMonthCustomFieldId,
                isInclusiveTax:           serviceOrderDraft.isInclusiveTax,
                pricelistId:              serviceOrderDraft.pricelistId,
                soStatus:                 serviceOrderDraft.action === 'confirm' ? 'confirmed' : 'draft',
                customerNotes:            serviceOrderDraft.notes || undefined,
                terms:                    serviceOrderDraft.terms,
                price:                    serviceOrderDraft.total,
              } : {}),
            })
              .then(() => {
                // See CreateBookingDesktop.tsx's matching comment —
                // ServiceOrderModal has no UI path that ever produces
                // action:'confirm', so every SO created through it lands as a
                // Zoho draft regardless of what the admin picked, even though
                // this booking is always confirmed immediately
                // (createBookingWithAvailabilityCheck always calls
                // confirmBooking internally). Confirm the SO here instead.
                return confirmServiceOrder(wixBookingId);
              })
              .catch(err => console.error('[BookingForm] Zoho SO create/confirm failed (non-fatal):', err));
          }
        }

        if (failedOn) {
          const failedOnDate = (failedOn as { date: Date; reason: string }).date.toLocaleDateString(intlLocale(), { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
          presentAlert({
            header: tr('pages.bookingSystem.BookingForm.someBookingsWereNotCreated'),
            message: `${createdCount} of ${occurrences} bookings were created. The one on ${failedOnDate} could not be: ${(failedOn as { date: Date; reason: string }).reason}`,
            cssClass: 'logout-alert',
            buttons: ['OK'],
          });
        }

        // Notify all tabs that a booking was created
        console.log('🚀 BOOKING: Sending refresh trigger signal');
        DataStorageService.storeData('booking-refresh-trigger', Date.now().toString());
        DataStorageService.notifyBookingChanged();
        console.log('🚀 BOOKING: Refresh signal sent successfully');

        // Give Wix API time to process and propagate the changes
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Show success message and navigate away
      handleClose();
    } catch (error) {
      console.error('Error saving booking:', error);
      // TODO: Show error message to user
    } finally {
      setIsSaving(false);
    }
  };

  // Preload service type images
  useEffect(() => {
    let isMounted = true;
    const fetchAndPreloadServiceTypes = async () => {
      setServiceTypesLoading(true);
      try {
        const dataService = DataServiceFactory.getService();
        const servicesData = await dataService.getServiceTypes();
        // Preload all images
        await Promise.all(
          servicesData.map((svc: { imageUrl?: string }) =>
            new Promise<void>(resolve => {
              if (!svc.imageUrl) return resolve();
              const img = new window.Image();
              img.onload = () => resolve();
              img.onerror = () => resolve();
              img.src = svc.imageUrl;
            })
          )
        );
        if (isMounted) setServiceTypeOptions(servicesData);
      } finally {
        if (isMounted) setServiceTypesLoading(false);
      }
    };
    fetchAndPreloadServiceTypes();
    return () => { isMounted = false; };
  }, []);

  // Pre-select the service type passed in from the Services browser
  // (Bookings.tsx) — new bookings only, and only once (doesn't override a
  // type the user has since changed). Computed during render (React's
  // "adjust state when a dependency changes" pattern) rather than as a
  // setState call inside an effect, since it's a pure derivation from
  // already-available props/state.
  if (!isEditing && initialServiceType && !serviceType) {
    setServiceType(initialServiceType);
  }

  // Set service thumbnail when service types are loaded and we have a
  // serviceType — computed during render for the same reason as above.
  const [prevThumbnailInputs, setPrevThumbnailInputs] = useState({ serviceType, serviceTypeOptions });
  if (prevThumbnailInputs.serviceType !== serviceType || prevThumbnailInputs.serviceTypeOptions !== serviceTypeOptions) {
    setPrevThumbnailInputs({ serviceType, serviceTypeOptions });
    if (serviceType && serviceTypeOptions.length > 0) {
      const norm = (s: string) => s.toLowerCase().replace(/[-\s]+/g, ' ').trim();
      const matchingService = serviceTypeOptions.find(service => norm(service.type) === norm(serviceType as string));
      setServiceThumbnail(matchingService?.imageUrl || '/assets/images/bucket.svg');
    }
  }

  // Load allowed resource types for the selected service
  useEffect(() => {
    let isMounted = true;
    const fetchServiceResourceTypes = async () => {
      // Wait for both service type and service options to be available
      if (!serviceType || serviceTypeOptions.length === 0) {
        setAllowedResourceTypeIds([]);
        return;
      }

      try {
        // Find the service ID for the selected service type (normalize dashes/spaces for Move-Out variants)
        const normalize = (s: string) => s.toLowerCase().replace(/[-\s]+/g, ' ').trim();
        const matchingService = serviceTypeOptions.find(s => normalize(s.type) === normalize(serviceType as string));
        if (!matchingService?.serviceId) {
          console.warn('⚠️ No serviceId found for', serviceType);
          setAllowedResourceTypeIds([]);
          return;
        }

        // Query the service details to get its resource types
        const result = await callAdminApi('getService', 'services', { id: matchingService.serviceId });
        console.log('🔍 Service details for', serviceType, ':', result);

        // Extract resource types from the serviceResources field
        const service = result as {
          serviceResources?: Array<{
            _id?: string;
            resourceType?: { _id?: string };
          }>;
        };

        const resourceTypeIds = (service.serviceResources || [])
          .map(item => item.resourceType?._id || item._id)
          .filter((id): id is string => Boolean(id));

        console.log('✅ Allowed resource type IDs for', serviceType, ':', resourceTypeIds);
        if (isMounted) {
          setAllowedResourceTypeIds(resourceTypeIds);
        }
      } catch (error) {
        console.error('❌ Error fetching service resource types:', error);
        if (isMounted) {
          // On error, show all resources (backwards compatible)
          setAllowedResourceTypeIds([]);
        }
      }
    };

    fetchServiceResourceTypes();
    return () => { isMounted = false; };
  }, [serviceType, serviceTypeOptions]);

  // Load resources from API
  useEffect(() => {
    let isMounted = true;
    const fetchResources = async () => {
      console.log('🔧 Booking.tsx: Starting to fetch resources...');
      setResourcesLoading(true);
      try {
        const dataService = DataServiceFactory.getService();
        console.log('🔧 Booking.tsx: DataService obtained');

        const groupedData = await dataService.resources.getResourcesGroupedByCategory();

        console.log('🔧 Booking.tsx: Resources fetched successfully');
        console.log('🔧 Booking.tsx: groupedData:', groupedData);

        if (isMounted) {
          // 'Unassigned' is the service's fallback bucket for resources with no type — not bookable here.
          const { Unassigned: _unassigned, ...bookable } = groupedData; // i18n-ignore: data key from the resources service
          setGroupedResources(bookable);
        }
      } catch (error) {
        console.error('❌ Booking.tsx: Error loading resources:', error);
        if (isMounted) {
          setGroupedResources({});
        }
      } finally {
        if (isMounted) setResourcesLoading(false);
      }
    };
    fetchResources();
    return () => { isMounted = false; };
  }, []);

  // Load cached contact list for the inline contact search dropdown
  useEffect(() => {
    getCachedContactList(() => dataService.contacts.getAllContacts())
      .then(result => {
        setAllContacts(result.data.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')));
      })
      .catch(() => { /* ignore */ });
  }, [dataService.contacts]);

  useEffect(() => {
    if (showContactSearch) {
      setTimeout(() => contactSearchRef.current?.setFocus(), 50);
    }
  }, [showContactSearch]);

  const filteredContacts = allContacts.filter(c => {
    const q = contactSearch.toLowerCase();
    if (!q) return true;
    return (
      (c.displayName || '').toLowerCase().includes(q) ||
      (`${c.firstName} ${c.lastName}`).toLowerCase().includes(q) ||
      c.primaryEmail.toLowerCase().includes(q) ||
      c.primaryPhone.includes(q)
    );
  }).slice(0, 40);

  // Fetch staff members + avatar map for the inline staff search dropdown
  useEffect(() => {
    let isMounted = true;
    const fetchStaff = async () => {
      setStaffFetchLoading(true);
      try {
        const svc = DataServiceFactory.getService();
        const staff = await svc.staff.getAllStaffMembers();
        if (!isMounted) return;
        setAllStaffMembers(staff);
        const avatarMap = new Map<string, string>();
        const contactIds = staff
          .map(m => (m.associatedWixIdentity as Record<string, unknown> | undefined)
            ?.identificationData as Record<string, unknown> | undefined)
          .map(id => id?.contactId as string | undefined)
          .filter((id): id is string => Boolean(id));
        if (contactIds.length) {
          const contacts = await Promise.all(
            contactIds.map(id => svc.contacts.getContactById(id).catch(() => null))
          );
          contacts.forEach(c => { if (c?.avatar) avatarMap.set(c._id as string, c.avatar); });
          staff.forEach(m => {
            const cId = ((m.associatedWixIdentity as Record<string, unknown> | undefined)
              ?.identificationData as Record<string, unknown> | undefined)?.contactId as string | undefined;
            if (cId && avatarMap.has(cId)) {
              avatarMap.set(m._id, avatarMap.get(cId)!);
            } else {
              const img = (m.mainMedia as Record<string, unknown> | undefined)?.image;
              if (img && typeof img === 'string') avatarMap.set(m._id, img);
            }
          });
        }
        if (isMounted) setStaffAvatarMap(avatarMap);
      } catch {
        if (isMounted) setAllStaffMembers([]);
      } finally {
        if (isMounted) setStaffFetchLoading(false);
      }
    };
    fetchStaff();
    return () => { isMounted = false; };
  }, []);

  const filteredStaff = allStaffMembers.filter(s => s.name.toLowerCase().includes(staffSearch.toLowerCase()));

  return (
    <IonModal
      id="booking-modal"
      className={styles.fullscreenModal}
      isOpen={isOpen}
      onDidDismiss={onClose}
      canDismiss={true}
      backdropDismiss={true}
      keepContentsMounted={false}
      handle={false}
    >
      {/* Portal target for the Date/Time/Duration/Repeat/Resource/Staff/Address dropdowns */}
      <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
      <BasePageWithHeader
          title={isEditing ? tr('pages.bookingSystem.BookingForm.editBooking') : tr('pages.bookingSystem.BookingForm.createNewBooking')}
          toolbarClassName={styles.toolbar}
          startButtons={
            <IonButton shape="round" className={styles.closeButton} onClick={handleClose}>
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          }
        >
          <IonContent scrollY={false} className={styles.content}>
            <IonGrid className={styles.formContainer}>

              {/* Contact card */}
              <IonCard className={styles.card}>
                <IonCardContent className={styles.cardBody}>
                  <IonLabel className={styles.cardTitle}><IonIcon icon={personOutline} /><h2>{tr('pages.bookingSystem.BookingForm.contact')}</h2></IonLabel>

                  {contact ? (
                    <>
                      {!showContactSearch ? (
                        <IonItem key="contact-display" lines="none" className={styles.contactDisplay}>
                          <IonAvatar slot="start">
                            <img src={proxyImage(contact.avatarUrl ?? '') || getDefaultAvatar(contact.name)} alt={contact.name} />
                          </IonAvatar>
                          <IonLabel>
                            <h2>{contact.name}</h2>
                            {contact.email && <p>{contact.email}</p>}
                            {contact.phone && <p>{contact.phone}</p>}
                          </IonLabel>
                          <IonButton
                            ref={(el) => { contactFieldRef.current = el; }}
                            fill="clear"
                            slot="end"
                            className={styles.changeBtn}
                            onClick={() => { setShowContactSearch(true); setContactSearch(''); }}
                          >{tr('pages.bookingSystem.BookingForm.change')}
                          </IonButton>
                        </IonItem>
                      ) : (
                        <IonItem key="contact-search" ref={(el) => { contactFieldRef.current = el; }} lines="none" className={styles.searchBox}>
                          <IonIcon icon={searchOutline} slot="start" />
                          <IonInput
                            ref={contactSearchRef}
                            value={contactSearch}
                            onIonInput={(e) => setContactSearch(e.detail.value ?? '')}
                            placeholder={tr('pages.bookingSystem.BookingForm.searchByNameEmailOrPhone')}
                          />
                          <IonIcon icon={closeOutline} slot="end" style={{ cursor: 'pointer' }} onClick={closeContactPicker} />
                        </IonItem>
                      )}
                      <PickerDropdown
                        isOpen={showContactSearch}
                        fieldRef={contactFieldRef}
                        portalRef={pickerPortalRef}
                        close={closeContactPicker}
                        align="left"
                        matchWidth
                        className={styles.calendarDropdown}
                      >
                        {filteredContacts.length > 0 ? (
                          <IonList lines="none" className={styles.dropdownList}>
                            {filteredContacts.map(c => (
                              <IonItem key={c.id} button detail={false} lines="none" className={styles.dropdownItem} onClick={() => handleContactSelect(c)}>
                                <IonAvatar slot="start">
                                  <img src={proxyImage(c.avatar ?? '') || getDefaultAvatar(c.displayName || `${c.firstName} ${c.lastName}`.trim())} alt="" />
                                </IonAvatar>
                                <IonLabel>
                                  <h3>{c.displayName || `${c.firstName} ${c.lastName}`.trim()}</h3>
                                  {(c.primaryEmail || c.primaryPhone) && <p>{c.primaryEmail || c.primaryPhone}</p>}
                                </IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        ) : contactSearch.length > 0 ? (
                          <IonLabel className={styles.emptyState}>{tr('pages.bookingSystem.BookingForm.noContactsFound')}</IonLabel>
                        ) : null}
                      </PickerDropdown>

                      <IonRow>
                        <IonCol className={styles.pickerWrap}>
                          <IonItem
                            button
                            detail
                            detailIcon={chevronForwardOutline}
                            lines="none"
                            ref={addressFieldRef}
                            className={`${styles.fieldRow} ${!(selectedBookingAddress || contact.address) ? styles.fieldPlaceholder : ''}`}
                            onClick={() => { setShowAddressPicker(o => !o); setShowCustomAddressInput(false); }}
                          >
                            <IonIcon icon={locationOutline} slot="start" />
                            <IonLabel>
                              <p>{tr('pages.bookingSystem.BookingForm.serviceAddress')}</p>
                              <h3>{selectedBookingAddress || contact.address || tr('pages.bookingSystem.BookingForm.noAddressAvailable')}</h3>
                            </IonLabel>
                          </IonItem>
                          <PickerDropdown
                            isOpen={showAddressPicker}
                            fieldRef={addressFieldRef}
                            portalRef={pickerPortalRef}
                            close={closeAddressPicker}
                            align="left"
                            matchWidth
                            className={styles.calendarDropdown}
                          >
                            {!showCustomAddressInput ? (
                              <IonList lines="none" className={styles.dropdownList}>
                                {contactAddresses.map((address, i) => {
                                  const formattedAddress = [
                                    address.address.addressLine1,
                                    address.address.addressLine2,
                                    address.address.city,
                                    address.address.state,
                                    address.address.postalCode,
                                    address.address.country,
                                  ].filter(Boolean).join(', ');
                                  const isSelected = selectedBookingAddress === formattedAddress;
                                  return (
                                    <IonItem
                                      key={i}
                                      button
                                      detail={false}
                                      lines="none"
                                      className={`${styles.dropdownItem}${isSelected ? ` ${styles.dropdownItemActive}` : ''}`}
                                      onClick={() => {
                                        setSelectedBookingAddress(formattedAddress);
                                        if (contact) setContact({ ...contact, address: formattedAddress });
                                        setShowAddressPicker(false);
                                      }}
                                    >
                                      <IonLabel><h3>{formattedAddress}</h3></IonLabel>
                                    </IonItem>
                                  );
                                })}
                                <IonItem button detail={false} lines="none" className={styles.dropdownItem} onClick={() => { setShowCustomAddressInput(true); setCustomAddressValue(''); }}>
                                  <IonLabel>{tr('pages.bookingSystem.BookingForm.addCustomAddress')}</IonLabel>
                                </IonItem>
                              </IonList>
                            ) : (
                              <IonList lines="none" className={styles.customAddressForm}>
                                <IonItem lines="none">
                                  <IonLabel position="stacked">{tr('pages.bookingSystem.BookingForm.enterAddress')}</IonLabel>
                                  <IonInput
                                    value={customAddressValue}
                                    onIonInput={(e) => setCustomAddressValue(e.detail.value ?? '')}
                                    placeholder={tr('pages.bookingSystem.BookingForm.enterTheFullAddress')}
                                    clearInput
                                  />
                                </IonItem>
                                <IonRow>
                                  <IonCol size="6">
                                    <IonButton fill="outline" expand="block" onClick={() => { setShowCustomAddressInput(false); setCustomAddressValue(''); }}>{tr('pages.bookingSystem.BookingForm.back')}
                                    </IonButton>
                                  </IonCol>
                                  <IonCol size="6">
                                    <IonButton expand="block" disabled={!customAddressValue.trim()} onClick={handleCustomAddressSubmit}>{tr('pages.bookingSystem.BookingForm.addAddress')}
                                    </IonButton>
                                  </IonCol>
                                </IonRow>
                              </IonList>
                            )}
                          </PickerDropdown>
                        </IonCol>
                      </IonRow>
                    </>
                  ) : (
                    <IonRow>
                    <IonCol className={styles.pickerWrap}>
                      {!showContactSearch ? (
                        <IonButton
                          ref={(el) => { contactFieldRef.current = el; }}
                          expand="block"
                          fill="outline"
                          className={styles.dashedBtn}
                          onClick={() => { setShowContactSearch(true); setContactSearch(''); }}
                        >
                          <IonIcon slot="start" icon={personOutline} />{tr('pages.bookingSystem.BookingForm.selectContact')}
                        </IonButton>
                      ) : (
                        <IonItem ref={(el) => { contactFieldRef.current = el; }} lines="none" className={styles.searchBox}>
                          <IonIcon icon={searchOutline} slot="start" />
                          <IonInput
                            ref={contactSearchRef}
                            value={contactSearch}
                            onIonInput={(e) => setContactSearch(e.detail.value ?? '')}
                            placeholder={tr('pages.bookingSystem.BookingForm.searchByNameEmailOrPhone')}
                          />
                          <IonIcon icon={closeOutline} slot="end" style={{ cursor: 'pointer' }} onClick={closeContactPicker} />
                        </IonItem>
                      )}
                      <PickerDropdown
                        isOpen={showContactSearch}
                        fieldRef={contactFieldRef}
                        portalRef={pickerPortalRef}
                        close={closeContactPicker}
                        align="left"
                        matchWidth
                        className={styles.calendarDropdown}
                      >
                        {filteredContacts.length > 0 ? (
                          <IonList lines="none" className={styles.dropdownList}>
                            {filteredContacts.map(c => (
                              <IonItem key={c.id} button detail={false} lines="none" className={styles.dropdownItem} onClick={() => handleContactSelect(c)}>
                                <IonAvatar slot="start">
                                  <img src={proxyImage(c.avatar ?? '') || getDefaultAvatar(c.displayName || `${c.firstName} ${c.lastName}`.trim())} alt="" />
                                </IonAvatar>
                                <IonLabel>
                                  <h3>{c.displayName || `${c.firstName} ${c.lastName}`.trim()}</h3>
                                  {(c.primaryEmail || c.primaryPhone) && <p>{c.primaryEmail || c.primaryPhone}</p>}
                                </IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        ) : contactSearch.length > 0 ? (
                          <IonLabel className={styles.emptyState}>{tr('pages.bookingSystem.BookingForm.noContactsFound')}</IonLabel>
                        ) : null}
                      </PickerDropdown>
                    </IonCol>
                    </IonRow>
                  )}
                </IonCardContent>
              </IonCard>

              {/* Schedule card */}
              <IonCard className={styles.card}>
                <IonCardContent className={styles.cardBody}>
                  <IonLabel className={styles.cardTitle}><IonIcon icon={calendarOutline} /><h2>{tr('pages.bookingSystem.BookingForm.schedule')}</h2></IonLabel>

                  <IonRow className={styles.scheduleRow}>
                    <IonCol className={styles.pickerWrap}>
                      <IonItem
                        button
                        detail
                        detailIcon={chevronForwardOutline}
                        lines="none"
                        ref={dateFieldRef}
                        className={`${styles.fieldRow} ${!selectedDate ? styles.fieldPlaceholder : ''}`}
                        onClick={() => setShowDatePicker(o => !o)}
                      >
                        <IonIcon icon={calendarOutline} slot="start" />
                        <IonLabel>
                          <p>{tr('pages.bookingSystem.BookingForm.date')}</p>
                          <h3>
                            {selectedDate ? selectedDate.toLocaleDateString(intlLocale(), {
                              weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                            }) : tr('pages.bookingSystem.BookingForm.selectDate')}
                          </h3>
                        </IonLabel>
                      </IonItem>
                      <PickerDropdown isOpen={showDatePicker} fieldRef={dateFieldRef} portalRef={pickerPortalRef} close={closeDatePicker} align="left" className={styles.calendarDropdown}>
                        <MiniCalendarPicker
                          desktop
                          value={selectedDate}
                          onSelect={(date) => {
                            const newDate = new Date(date);
                            // Keep the chosen start time if there is one. If there is none yet, leave it
                            // empty — it used to be filled from the picked date's own clock time,
                            // which is midnight (00:00), so choosing a date "set" a start time of
                            // 12 AM by itself and made the time look filled in.
                            if (selectedTime) {
                              const [h, m] = selectedTime.split(':').map(Number);
                              newDate.setHours(h, m, 0, 0);
                            }
                            setSelectedDate(newDate);
                            setShowDatePicker(false);
                          }}
                        />
                      </PickerDropdown>
                    </IonCol>
                  </IonRow>

                  {/* Repeats: one full-width button until a repeat is chosen, then it splits into two
                      side by side — how often it repeats, and for how many times. Only when creating:
                      editing changes the one booking, never its series. */}
                  {!isEditing && (
                  <IonRow className={`${styles.fieldRowGroup} ${styles.scheduleRow}`}>
                    <IonCol className={styles.pickerWrap}>
                      <IonItem button detail detailIcon={chevronForwardOutline} lines="none" ref={repeatFieldRef} className={styles.fieldRow} onClick={() => setShowRepeatPicker(o => !o)}>
                        <IonIcon icon={repeatOutline} slot="start" />
                        <IonLabel>
                          <p>{tr('pages.bookingSystem.BookingForm.repeats')}</p>
                          <h3>{repeatOption}</h3>
                        </IonLabel>
                      </IonItem>
                      <PickerDropdown isOpen={showRepeatPicker} fieldRef={repeatFieldRef} portalRef={pickerPortalRef} close={closeRepeatPicker} align="left" matchWidth className={styles.calendarDropdown}>
                        <IonList lines="none" className={styles.dropdownList}>
                          {["Doesn't repeat", "Weekly", "Bi-weekly", "Every 3 weeks", "Every 4 weeks"].map(val => (
                            <IonItem
                              key={val}
                              button
                              detail={false}
                              lines="none"
                              className={`${styles.dropdownItem}${repeatOption === val ? ` ${styles.dropdownItemActive}` : ''}`}
                              onClick={() => { setRepeatOption(val); setShowRepeatPicker(false); }}
                            >
                              <IonLabel><h3>{val}</h3></IonLabel>
                            </IonItem>
                          ))}
                        </IonList>
                      </PickerDropdown>
                    </IonCol>

                    {doesRepeat && (
                      <IonCol className={styles.pickerWrap}>
                        <IonItem button detail detailIcon={chevronForwardOutline} lines="none" ref={repeatCountFieldRef} className={styles.fieldRow} onClick={() => setShowRepeatCountPicker(o => !o)}>
                          <IonIcon icon={syncOutline} slot="start" />
                          <IonLabel>
                            <p>{tr('pages.bookingSystem.BookingForm.occurrences')}</p>
                            <h3>{tr('pages.bookingSystem.BookingForm.times', { repeatCount })}</h3>
                          </IonLabel>
                        </IonItem>
                        <PickerDropdown isOpen={showRepeatCountPicker} fieldRef={repeatCountFieldRef} portalRef={pickerPortalRef} close={closeRepeatCountPicker} align="right" matchWidth className={styles.calendarDropdown}>
                          <IonList lines="none" className={styles.dropdownList}>
                            {REPEAT_COUNT_OPTIONS.map(val => (
                              <IonItem
                                key={val}
                                button
                                detail={false}
                                lines="none"
                                className={`${styles.dropdownItem}${repeatCount === val ? ` ${styles.dropdownItemActive}` : ''}`}
                                onClick={() => { setRepeatCount(val); setShowRepeatCountPicker(false); }}
                              >
                                <IonLabel><h3>{tr('pages.bookingSystem.BookingForm.times2', { val })}</h3></IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        </PickerDropdown>
                      </IonCol>
                    )}
                  </IonRow>
                  )}

                  {/* Start time + duration, side by side under Repeats */}
                  <IonRow className={`${styles.fieldRowGroup} ${styles.scheduleRow}`}>
                    <IonCol className={styles.pickerWrap}>
                      <IonItem
                        button
                        detail
                        detailIcon={chevronForwardOutline}
                        lines="none"
                        ref={timeFieldRef}
                        className={`${styles.fieldRow} ${!selectedTime ? styles.fieldPlaceholder : ''}`}
                        onClick={() => setShowTimePicker(o => !o)}
                      >
                        <IonIcon icon={timeOutline} slot="start" />
                        <IonLabel>
                          <p>{tr('pages.bookingSystem.BookingForm.startTime')}</p>
                          <h3>
                            {selectedTime && selectedTime.length >= 4 && selectedTime.includes(':') ?
                              new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString(intlLocale(), {
                                hour: 'numeric', minute: '2-digit', hour12: true
                              }) : tr('pages.bookingSystem.BookingForm.selectTime')}
                          </h3>
                        </IonLabel>
                      </IonItem>
                      <PickerDropdown isOpen={showTimePicker} fieldRef={timeFieldRef} portalRef={pickerPortalRef} close={closeTimePicker} align="left" className={styles.timeDropdown}>
                        <GcalTimePicker
                          desktop
                          value={selectedTime || '09:00'}
                          onSelect={(t) => { handleTimeSelect(t); setShowTimePicker(false); }}
                          onClose={() => setShowTimePicker(false)}
                        />
                      </PickerDropdown>
                    </IonCol>

                    <IonCol className={styles.pickerWrap}>
                      <IonItem
                        button
                        detail
                        detailIcon={chevronForwardOutline}
                        lines="none"
                        ref={durationFieldRef}
                        className={`${styles.fieldRow} ${duration === 0 ? styles.fieldPlaceholder : ''}`}
                        onClick={() => setShowDurationPicker(o => !o)}
                      >
                        <IonIcon icon={timerOutline} slot="start" />
                        <IonLabel>
                          <p>{tr('pages.bookingSystem.BookingForm.duration')}</p>
                          <h3>{duration > 0 ? `${Math.floor(duration / 60)}h${duration % 60 > 0 ? ` ${duration % 60}m` : ''}` : tr('pages.bookingSystem.BookingForm.selectDuration')}</h3>
                        </IonLabel>
                      </IonItem>
                      <PickerDropdown isOpen={showDurationPicker} fieldRef={durationFieldRef} portalRef={pickerPortalRef} close={closeDurationPicker} align="left" matchWidth className={styles.calendarDropdown}>
                        <IonList lines="none" className={styles.dropdownList}>
                          {[15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180, 195, 210, 225, 240, 255, 270, 285, 300, 315, 330, 345, 360].map(val => {
                            const h = Math.floor(val / 60);
                            const m = val % 60;
                            const label = m > 0 ? `${h}h ${m}m` : `${h}h`;
                            return (
                              <IonItem
                                key={val}
                                button
                                detail={false}
                                lines="none"
                                className={`${styles.dropdownItem}${duration === val ? ` ${styles.dropdownItemActive}` : ''}`}
                                onClick={() => { setDuration(val); setShowDurationPicker(false); }}
                              >
                                <IonLabel><h3>{label}</h3></IonLabel>
                              </IonItem>
                            );
                          })}
                        </IonList>
                      </PickerDropdown>
                    </IonCol>
                  </IonRow>
                </IonCardContent>
              </IonCard>

              {/* Service & Order card */}
              <IonCard className={styles.card}>
                <IonCardContent className={styles.cardBody}>
                  <IonLabel className={styles.cardTitle}><IonIcon icon={cubeOutline} /><h2>{tr('pages.bookingSystem.BookingForm.serviceOrder')}</h2></IonLabel>

                  {!serviceType ? (
                    <>
                      {/* A new booking is filled in in order: the schedule (date + start time) comes
                          first, so the service and order can't be chosen until it is set. */}
                      <IonButton
                        expand="block"
                        fill="outline"
                        className={styles.dashedBtn}
                        disabled={!isScheduleSet}
                        onClick={() => { setIsAddingAdditionalService(false); void openServiceOrderModal(); }}
                      >
                        <IonIcon slot="start" icon={addCircle} />{tr('pages.bookingSystem.BookingForm.chooseServiceConfigureOrder')}
                      </IonButton>
                      {!isScheduleSet && (
                        <IonNote className={styles.orderHint}>{tr('pages.bookingSystem.BookingForm.selectADateAndStartTime')}</IonNote>
                      )}
                    </>
                  ) : (
                    <IonItem lines="none" className={styles.selectedServiceItem}>
                      <IonThumbnail slot="start" className={styles.serviceThumbnail}>
                        <img src={serviceThumbnail || '/assets/images/bucket.svg'} alt={serviceType} loading="eager" />
                      </IonThumbnail>
                      <IonLabel><h3>{translateDynamic(serviceType)}</h3></IonLabel>
                      <IonButton fill="clear" slot="end" className={styles.changeBtn} onClick={() => { setIsAddingAdditionalService(false); void openServiceOrderModal(); }}>{tr('pages.bookingSystem.BookingForm.change')}
                      </IonButton>
                    </IonItem>
                  )}

                  {additionalServices.map((service, index) => (
                    <IonItem key={`additional-service-${index}`} lines="none" className={styles.selectedServiceItem}>
                      <IonThumbnail slot="start" className={styles.serviceThumbnail}>
                        <img src={service.thumbnail} alt={service.type} loading="eager" />
                      </IonThumbnail>
                      <IonLabel><h3>{service.type}</h3></IonLabel>
                      <IonButton fill="clear" slot="end" className={styles.removeBtn} onClick={(e) => { e.stopPropagation(); handleRemoveAdditionalService(index); }}>
                        <IonIcon icon={closeOutline} />
                      </IonButton>
                    </IonItem>
                  ))}

                  {serviceType && (
                    <IonButton
                      expand="block"
                      fill="outline"
                      className={styles.addLineBtn}
                      onClick={() => { setIsAddingAdditionalService(true); setCurrentAdditionalServiceIndex(-1); setIsServiceTypeModalOpen(true); }}
                    >
                      <IonIcon slot="start" icon={addOutline} />{tr('pages.bookingSystem.BookingForm.addAnotherService')}
                    </IonButton>
                  )}
                </IonCardContent>
              </IonCard>

              {/* Assignment card */}
              <IonCard className={styles.card}>
                <IonCardContent className={styles.cardBody}>
                  <IonLabel className={styles.cardTitle}><IonIcon icon={peopleOutline} /><h2>{tr('pages.bookingSystem.BookingForm.assignment')}</h2></IonLabel>

                  <IonRow>
                    <IonCol className={styles.pickerWrap}>
                      <IonItem
                        button
                        detail
                        detailIcon={chevronForwardOutline}
                        lines="none"
                        ref={resourceFieldRef}
                        className={`${styles.fieldRow} ${!resourceOption ? styles.fieldPlaceholder : ''}`}
                        onClick={() => setShowResourcePicker(o => !o)}
                      >
                        <IonIcon icon={constructOutline} slot="start" />
                        <IonLabel>
                          <p>{tr('pages.bookingSystem.BookingForm.resource')}</p>
                          <h3>{resourceOption || (resourcesLoading ? tr('pages.bookingSystem.BookingForm.loading') : tr('pages.bookingSystem.BookingForm.selectResource'))}</h3>
                        </IonLabel>
                      </IonItem>
                      <PickerDropdown isOpen={showResourcePicker} fieldRef={resourceFieldRef} portalRef={pickerPortalRef} close={closeResourcePicker} align="left" matchWidth className={styles.calendarDropdown}>
                        <IonList lines="none" className={styles.dropdownList}>
                          {resourcesLoading && <IonLabel className={styles.emptyState}>{tr('pages.bookingSystem.BookingForm.loadingResources')}</IonLabel>}
                          {!resourcesLoading && Object.keys(groupedResources).length === 0 && (
                            <IonLabel className={styles.emptyState}>{tr('pages.bookingSystem.BookingForm.noResourcesAvailable')}</IonLabel>
                          )}
                          {Object.entries(groupedResources).map(([category, categoryResources]) => {
                            const filteredResources = allowedResourceTypeIds.length > 0
                              ? categoryResources.filter(resource => resource.type && allowedResourceTypeIds.includes(resource.type))
                              : categoryResources;
                            if (filteredResources.length === 0) return null;
                            return (
                              <React.Fragment key={category}>
                                <IonItem lines="none" className={styles.dropdownGroupHeader}>
                                  <IonLabel><h3>{translateDynamic(category)}</h3></IonLabel>
                                </IonItem>
                                {filteredResources.map(resource => {
                                  const isSelected = resourceOption === (resource.name || resource._id);
                                  return (
                                    <IonItem
                                      key={resource._id}
                                      button
                                      detail={false}
                                      lines="none"
                                      className={`${styles.dropdownItem}${isSelected ? ` ${styles.dropdownItemActive}` : ''}`}
                                      onClick={() => { setResourceOption(resource.name || resource._id); setShowResourcePicker(false); }}
                                    >
                                      <IonLabel><h3>{resource.name || resource._id}</h3></IonLabel>
                                    </IonItem>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </IonList>
                      </PickerDropdown>
                    </IonCol>
                  </IonRow>

                  <IonRow className={styles.fieldRowGroup}>
                    <IonCol className={styles.pickerWrap}>
                      <IonItem
                        button
                        detail
                        detailIcon={chevronForwardOutline}
                        lines="none"
                        ref={staffFieldRef}
                        className={`${styles.fieldRow} ${!staffMembers[0] ? styles.fieldPlaceholder : ''}`}
                        onClick={() => {
                          setCurrentStaffIndex(0);
                          setIsAddingAdditionalStaff(false);
                          setStaffSearch('');
                          setShowStaffPicker(o => !o || isAddingAdditionalStaff);
                        }}
                      >
                        <IonIcon icon={personOutline} slot="start" />
                        <IonLabel>
                          <p>{tr('pages.bookingSystem.BookingForm.staffMember')}</p>
                          <h3>{staffMembers[0] || tr('pages.bookingSystem.BookingForm.selectStaffMember')}</h3>
                        </IonLabel>
                      </IonItem>
                      <PickerDropdown
                        isOpen={showStaffPicker && !isAddingAdditionalStaff}
                        fieldRef={staffFieldRef}
                        portalRef={pickerPortalRef}
                        close={closeStaffPicker}
                        align="left"
                        matchWidth
                        className={styles.calendarDropdown}
                      >
                        <IonItem lines="none" className={styles.searchBox}>
                          <IonIcon icon={searchOutline} slot="start" />
                          <IonInput
                            placeholder={tr('pages.bookingSystem.BookingForm.searchStaff')}
                            value={staffSearch}
                            onIonInput={(e) => setStaffSearch(e.detail.value ?? '')}
                          />
                        </IonItem>
                        <IonList lines="none" className={styles.dropdownList}>
                          {staffFetchLoading ? (
                            <IonLabel className={styles.emptyState}>{tr('pages.bookingSystem.BookingForm.loading')}</IonLabel>
                          ) : filteredStaff.map(s => (
                            <IonItem
                              key={s._id}
                              button
                              detail={false}
                              lines="none"
                              className={`${styles.dropdownItem}${staffMembers[0] === s.name ? ` ${styles.dropdownItemActive}` : ''}`}
                              onClick={() => handleStaffMemberSelect(s.name)}
                            >
                              <IonAvatar slot="start">
                                <img src={proxyImage(staffAvatarMap.get(s._id) ?? '') || getDefaultAvatar(s.name)} alt={s.name} />
                              </IonAvatar>
                              <IonLabel><h3>{s.name}</h3></IonLabel>
                            </IonItem>
                          ))}
                        </IonList>
                      </PickerDropdown>
                    </IonCol>
                  </IonRow>

                  {staffMembers.slice(1).map((staff, index) => (
                    <IonItem key={`staff-${index + 1}`} lines="none" className={`${styles.fieldRow} ${styles.fieldRowStatic}`}>
                      <IonIcon icon={personOutline} slot="start" />
                      <IonLabel>
                        <p>{tr('pages.bookingSystem.BookingForm.additionalStaff')}</p>
                        <h3>{staff}</h3>
                      </IonLabel>
                      <IonButton fill="clear" slot="end" className={styles.removeBtn} onClick={() => handleRemoveStaffMember(index + 1)}>
                        <IonIcon icon={closeOutline} />
                      </IonButton>
                    </IonItem>
                  ))}

                  {staffMembers.length > 0 && (
                    <IonRow className={styles.fieldRowGroup}>
                      <IonCol className={styles.pickerWrap}>
                        <IonButton expand="block" fill="outline" ref={staffAddFieldRef} className={styles.addLineBtn} onClick={handleAddStaffMember}>
                          <IonIcon slot="start" icon={addOutline} />{tr('pages.bookingSystem.BookingForm.addMoreStaff')}
                        </IonButton>
                        <PickerDropdown
                          isOpen={showStaffPicker && isAddingAdditionalStaff}
                          fieldRef={staffAddFieldRef}
                          portalRef={pickerPortalRef}
                          close={closeStaffPicker}
                          align="left"
                          matchWidth
                          className={styles.calendarDropdown}
                        >
                          <IonItem lines="none" className={styles.searchBox}>
                            <IonIcon icon={searchOutline} slot="start" />
                            <IonInput
                              placeholder={tr('pages.bookingSystem.BookingForm.searchStaff')}
                              value={staffSearch}
                              onIonInput={(e) => setStaffSearch(e.detail.value ?? '')}
                            />
                          </IonItem>
                          <IonList lines="none" className={styles.dropdownList}>
                            {staffFetchLoading ? (
                              <IonLabel className={styles.emptyState}>{tr('pages.bookingSystem.BookingForm.loading')}</IonLabel>
                            ) : filteredStaff.map(s => (
                              <IonItem key={s._id} button detail={false} lines="none" className={styles.dropdownItem} onClick={() => handleStaffMemberSelect(s.name)}>
                                <IonAvatar slot="start">
                                  <img src={proxyImage(staffAvatarMap.get(s._id) ?? '') || getDefaultAvatar(s.name)} alt={s.name} />
                                </IonAvatar>
                                <IonLabel><h3>{s.name}</h3></IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        </PickerDropdown>
                      </IonCol>
                    </IonRow>
                  )}
                </IonCardContent>
              </IonCard>

              {/* Notes card */}
              <IonCard ref={notesCardRef} className={styles.card}>
                <IonCardContent className={styles.cardBody}>
                  <IonLabel className={styles.cardTitle}><h2>{tr('pages.bookingSystem.BookingForm.notes')}</h2></IonLabel>

                  {!notesAdded ? (
                    <IonButton expand="block" fill="outline" className={styles.addLineBtn} onClick={handleAddNotes}>
                      <IonIcon slot="start" icon={addOutline} />{tr('pages.bookingSystem.BookingForm.addBookingNotes')}
                    </IonButton>
                  ) : (
                    <>
                      <IonTextarea
                        ref={notesTextareaRef}
                        className={styles.textarea}
                        value={notes}
                        onIonInput={(e) => setNotes(e.detail.value ?? '')}
                        placeholder={tr('pages.bookingSystem.BookingForm.addAnySpecialInstructionsOrNotes')}
                        rows={4}
                      />
                      <IonButton fill="clear" className={styles.removeLinkBtn} onClick={handleDeleteNotes}>
                        <IonIcon slot="start" icon={trashOutline} />{tr('pages.bookingSystem.BookingForm.removeNotes')}
                      </IonButton>
                    </>
                  )}
                </IonCardContent>
              </IonCard>

            </IonGrid>

            {/* Service Order Modal */}
            <ServiceOrderModal
              isOpen={isServiceOrderModalOpen}
              onClose={() => { setIsServiceOrderModalOpen(false); setSoNumberError(undefined); }}
              onConfirm={handleServiceOrderConfirm}
              serviceName={String(serviceType)}
              customerName={contact?.name || ''}
              contactId={contact?.id || ''}
              bookingId={isEditing ? bookingId : ''}
              catalogRate={catalogRate}
              soNumberError={soNumberError}
              initialDraft={serviceOrderDraft}
              defaultDate={selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : undefined}
            />

            {/* Service Type Modal */}
            <ServiceTypeModal
              isOpen={isServiceTypeModalOpen}
              onClose={() => setIsServiceTypeModalOpen(false)}
              onSelectServiceType={handleServiceTypeSelectWithIndex}
              selectedServiceType={serviceType}
              services={serviceTypeOptions}
              loading={serviceTypesLoading}
            />
          </IonContent>

          <IonFooter ref={setFooterEl} className={styles.footer}>
            <IonCard className={styles.footerWrapper}>
              <IonRow className={styles.paymentStatus}>
                <IonLabel>
                  <p className={styles.amountLabel}>{tr('pages.bookingSystem.BookingForm.totalPrice')}</p>
                  <p className={styles.taxNote}>{tr('pages.bookingSystem.BookingForm.tax')}{(taxAmount ?? price * 0.15).toFixed(2)}{tr('pages.bookingSystem.BookingForm.included')}</p>
                  <h2 className={styles.amount}>${price.toFixed(2)}</h2>
                </IonLabel>
                <IonButton
                  className={styles.bookButton}
                  disabled={!contact || !serviceType || !resourceOption || staffMembers.length === 0 || !selectedTime || duration === 0 || !selectedDate || resourcesLoading || isSaving}
                  onClick={handleCreateBooking}
                >
                  {isSaving && <IonSpinner name="bubbles" style={{ marginRight: '6px' }} />}
                  {isEditing ? tr('pages.bookingSystem.BookingForm.saveChanges') : tr('pages.bookingSystem.BookingForm.scheduleBooking')}
                </IonButton>
              </IonRow>
            </IonCard>
          </IonFooter>
        </BasePageWithHeader>
    </IonModal>
  );
};

export default Booking;